import { flash, SubmitButton, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import { FormField, InputField, requiredNumber, requiredString, TextareaField } from "common";
import { FormikErrors, FormikProps } from "formik";
import { produce } from "immer";
import { pick } from "lodash";
import React, { useEffect, useRef } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    CollectionNode,
    Experience,
    MeetingLocation,
    Permission,
    ProductObject,
    ScheduleType,
    Seller,
    VirtualMeetingPreference,
} from "types";
import { boolean, object, string } from "yup";
import { api } from "../../../api/api";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { isPurchaseItemizationEnabled } from "../../../models/Seller";
import { Form } from "../../Common/Form";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLengthCounter } from "../../Field/ContentLengthCounter";
import { ContentLayout } from "../../Layout/ContentLayout";
import { ProductAttachmentsUpload } from "../ProductAttachmentsUpload";
import { getProductCollectionPayload, ProductCollectionSelector } from "../ProductCollectionSelector";
import { EmailFooter } from "./EmailFooter";
import { ExperienceChecklist } from "./ExperienceChecklist";
import { MeetingLocationField } from "@/components/Product/MeetingLocationField";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useModal } from "@/hooks/useVisibility";
import {
    applyMapPreferenceToPayload,
    getDefaultExperience,
    hasMappedMeetingLocation,
    sanitizeExperiencePayload,
} from "@/models/Experience";

const DESCRIPTIVE_CONTENT_FIELDS: Array<keyof ExperienceDescriptionFormValues> = [
    "name",
    "virtualMeeting",
    "excerpt",
    "desc",
    "customerNamePreference",
    "other",
    "pickupAddress",
    "geo",
    "enableMap",
    "meetingLocation",
    "meetingLocationInstructions",
    "cancellation",
    "emailFooter",
    "included",
    "notIncluded",
];

export const getValidationSchema = () =>
    object().shape({
        name: requiredString(),
        virtualMeeting: object().shape({
            enabled: boolean(),
        }),
        excerpt: requiredString().max(350, "must have less than 350 characters"),
        desc: requiredString(),
        customerNamePreference: string(),
        other: string(),
        enableMap: boolean(),
        pickupAddress: string()
            .trim()
            .when(["virtualMeeting", "meetingLocation", "enableMap"], {
                is: (
                    virtualMeeting: VirtualMeetingPreference,
                    meetingLocation?: MeetingLocation,
                    enableMap?: boolean,
                ) => !virtualMeeting?.enabled && (!enableMap || !meetingLocation?.formattedAddress),
                then: (schema) => schema.required("is required"),
            }),
        geo: object()
            .nullable()
            .default(undefined)
            .when(["virtualMeeting", "meetingLocation", "enableMap"], {
                is: (
                    virtualMeeting: VirtualMeetingPreference,
                    meetingLocation?: MeetingLocation,
                    enableMap?: boolean,
                ) => !!enableMap && !meetingLocation && !virtualMeeting?.enabled,
                then: (schema) => schema.shape({ lat: requiredNumber(), lng: requiredNumber() }),
            }),
        meetingLocation: object()
            .nullable()
            .default(undefined)
            .when(["virtualMeeting", "enableMap"], {
                is: (virtualMeeting: VirtualMeetingPreference, enableMap?: boolean) =>
                    !!enableMap && !virtualMeeting?.enabled,
                then: (schema) =>
                    schema.shape({
                        geo: object().shape({
                            lat: requiredNumber(),
                            lng: requiredNumber(),
                        }),
                    }),
            }),
    });

interface ExperienceDescriptionFormValues extends Omit<Experience, "id"> {
    id?: ID; // for new experience id wil be optional
    enableMap?: boolean;
}

export const ExperienceDescriptiveContent = ({ experience }: { experience?: Experience | undefined }) => {
    const seller = useSeller();
    const history = useHistory();
    const invalidate = useInvalidate();
    const collectionModal = useModal();

    const formikRef = useRef<FormikProps<ExperienceDescriptionFormValues>>(null);

    const { isGranted } = useAuthorization();
    const hasVirtualMeetingPlugin = isGranted(Permission.VIRTUAL_MEETINGS_VIEW);
    const showCollectionModification = isGranted(Permission.MODIFY_COLLECTIONS);
    const showVirtualMeetingToggle = experience?.virtualMeeting?.enabled;

    // Fetch collections associated with this product
    const { data: addedCollections, isLoading: isLoadingAddedCollections } = useAutoFetchCollection<CollectionNode>(
        "collectionNodes",
        experience?.id ? { product: experience.id } : undefined,
        { enabled: !!experience?.id && showCollectionModification },
    );

    // Convert to paths for initial form values
    const addedCollectionPaths = addedCollections.map((node) => node.path ?? "").filter(Boolean);

    const { mutateAsync: saveCollections } = useMutation(
        async ({ newPaths, experienceId }: { newPaths: string[]; experienceId: string }) => {
            const collectionUpdateOperations = getProductCollectionPayload({
                newPaths,
                oldPaths: addedCollectionPaths,
                addedCollections,
                productId: experienceId,
            });

            if (collectionUpdateOperations.length > 0) {
                await api.patch("/collectionNodes", { ops: collectionUpdateOperations });
                invalidate("collectionNodes");
            }
        },
    );

    const { mutateAsync: save } = useMutation(async (payload: any) => {
        const experiencePayload = sanitizeExperiencePayload(applyMapPreferenceToPayload(payload));

        return experience?.id
            ? api.put(`/experiences/${experience.id}`, experiencePayload)
            : api.post("/experiences", experiencePayload);
    });

    const onSave = async (values: any) => {
        if (values.virtualMeeting?.enabled) {
            values.arrivalOffset = 0;
        }

        const response: any = await save(values);
        const { data } = response;
        const experienceId = data.id;

        // Save collection changes after experience is created/updated
        const hasCollectionChanges =
            showCollectionModification &&
            values.collections &&
            Array.isArray(values.collections) &&
            JSON.stringify([...values.collections].sort()) !== JSON.stringify([...addedCollectionPaths].sort());

        if (hasCollectionChanges) {
            await saveCollections({ newPaths: values.collections, experienceId });
        }

        invalidate("products");
        invalidate("experiences", experienceId);
        if (experience?.id) {
            flash.success("Successfully updated");
            formikRef.current?.setTouched({}); // reset touched fields
        } else {
            history.push(`/products/experiences/${experienceId}/schedules`);
        }
    };

    const handleCancel = () => {
        history.push(`/products/experiences`);
    };

    useEffect(() => {
        // When an attachment gets created / modified / deleted, we are invalidating the experience, insuch case
        // if there are any unsaved fields, we should persist them. This block will persist those unsaved changes.
        const shouldPersistFields = !!(formikRef.current && Object.keys(formikRef.current.touched).length > 0);
        if (experience && shouldPersistFields) {
            formikRef.current.setValues(
                getInitialValues(seller, {
                    ...experience,
                    ...pick(formikRef.current.values, DESCRIPTIVE_CONTENT_FIELDS),
                }),
            );
            formikRef.current.setTouched(formikRef.current.touched);
        }
    }, [seller, experience]);

    return (
        <div className="p-8">
            <h2>Description Content</h2>

            <div className="flex space-x-2">
                <div className="flex max-w-[260px] flex-col py-10">
                    <h3>Experience Description </h3>
                    <span>
                        Important details that will be presented to your customer throughout the booking process
                    </span>
                </div>

                <div className="flex-grow">
                    <XolaFormik
                        enableReinitialize
                        enablePrompt
                        validateOnChange
                        validateOnBlur={false}
                        validationSchema={getValidationSchema()}
                        initialValues={getInitialValues(seller, experience, addedCollectionPaths)}
                        innerRef={formikRef}
                        onSubmit={onSave}
                    >
                        {({ values, setFieldValue, isSubmitting, handleSubmit, errors }: any) => {
                            const handleEnableMapChange = (isEnabled: boolean) => {
                                setFieldValue("enableMap", isEnabled);

                                if (!isEnabled && !values.pickupAddress) {
                                    setFieldValue("pickupAddress", values.meetingLocation?.formattedAddress ?? "");
                                }
                            };

                            const clearMappedLocation = () => {
                                setFieldValue("meetingLocation", undefined);
                                setFieldValue("geo", { lat: null, lng: null });
                            };

                            const handleAddressChange = (address: string) => {
                                setFieldValue("pickupAddress", address);

                                if (!values.enableMap) {
                                    clearMappedLocation();
                                }
                            };

                            const handleSelectAddress = (address: string) => {
                                setFieldValue("pickupAddress", address);
                                clearMappedLocation();
                            };

                            const handleClear = () => {
                                setFieldValue("pickupAddress", "");

                                if (!values.enableMap) {
                                    clearMappedLocation();
                                } else if (values.meetingLocation?.formattedAddress) {
                                    setFieldValue("meetingLocation", { geo: values.meetingLocation.geo });
                                }
                            };

                            return (
                                <ContentLayout>
                                    <Form>
                                        <fieldset className="mb-16 flex-1 space-y-12">
                                            <div className="space-y-4">
                                                {showCollectionModification && (
                                                    <FormField name="collections" label="">
                                                        <ProductCollectionSelector
                                                            addedCollectionPaths={addedCollectionPaths}
                                                            isLoading={isLoadingAddedCollections}
                                                            value={values.collections ?? []}
                                                            isOpen={collectionModal.isVisible}
                                                            onChange={(collections: string[]) => {
                                                                setFieldValue("collections", collections);
                                                            }}
                                                            onOpenChange={(isOpen) =>
                                                                isOpen ? collectionModal.show() : collectionModal.hide()
                                                            }
                                                        />
                                                    </FormField>
                                                )}

                                                <FormField
                                                    name="name"
                                                    label="Title"
                                                    tooltip="This is the name of the experience you offer customers to book online"
                                                >
                                                    <InputField isRequired trim={false} />
                                                    {(hasVirtualMeetingPlugin || showVirtualMeetingToggle) && (
                                                        <div className="mt-2 flex space-x-2">
                                                            <Switch
                                                                size="medium"
                                                                isChecked={values.virtualMeeting?.enabled}
                                                                onChange={(value: boolean) =>
                                                                    setFieldValue("virtualMeeting", {
                                                                        ...values.virtualMeeting,
                                                                        enabled: value,
                                                                    })
                                                                }
                                                            />
                                                            <span>This is an Online Experience</span>
                                                        </div>
                                                    )}
                                                </FormField>
                                                <FormField
                                                    name="excerpt"
                                                    label="Excerpt"
                                                    tooltip="This is the text that will appear to your customers when they are checking out online. This text does not show on the confirmation email"
                                                >
                                                    <TextareaField isRequired rows={6} />
                                                    <ContentLengthCounter value={values.excerpt} maxLength={350} />
                                                </FormField>
                                                <FormField
                                                    name="desc"
                                                    label="Description"
                                                    tooltip="This text is used when participating in Xola's optional distribution channels, meaning it will not be displayed to your customers when making a normal booking through a Xola checkout window"
                                                >
                                                    <TextareaField isRequired rows={6} />
                                                    <ContentLengthCounter value={values.desc} />
                                                </FormField>

                                                {!values.virtualMeeting?.enabled && (
                                                    <div className="flex flex-col space-y-2">
                                                        <div className="flex space-x-2">
                                                            <Switch
                                                                size="medium"
                                                                isChecked={!!values.enableMap}
                                                                data-testid="enable-map-switch"
                                                                onChange={handleEnableMapChange}
                                                            />
                                                            <div>
                                                                <MeetingLocationLabel
                                                                    errors={errors}
                                                                    isMapEnabled={!!values.enableMap}
                                                                />
                                                                <span className="text-sm text-gray-dark">
                                                                    Enable and locate on map
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <MeetingLocationField
                                                            isMapEnabled={!!values.enableMap}
                                                            meetingLocation={values.meetingLocation}
                                                            pickupAddress={values.pickupAddress}
                                                            geo={values.geo}
                                                            hasError={!!(errors.meetingLocation || errors.geo)}
                                                            onSelectPlace={(meetingLocation) =>
                                                                setFieldValue("meetingLocation", meetingLocation)
                                                            }
                                                            onSelectAddress={handleSelectAddress}
                                                            onClear={handleClear}
                                                            onAddressChange={handleAddressChange}
                                                            onPinDrop={(geo) =>
                                                                setFieldValue("meetingLocation", { geo })
                                                            }
                                                            onTurnOff={() => {
                                                                if (
                                                                    !values.meetingLocation?.formattedAddress &&
                                                                    experience?.meetingLocation?.formattedAddress
                                                                ) {
                                                                    setFieldValue(
                                                                        "meetingLocation",
                                                                        experience.meetingLocation,
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                )}

                                                <ExperienceChecklist
                                                    included={values.included ?? []}
                                                    handleIncludedChange={(items: string[]) => {
                                                        setFieldValue("included", items ?? []);
                                                    }}
                                                    excluded={values.notIncluded ?? []}
                                                    handleNotIncludedChange={(items: string[]) => {
                                                        setFieldValue("notIncluded", items ?? []);
                                                    }}
                                                />

                                                <FormField
                                                    name="cancellation.policy"
                                                    label="Cancellation Policy"
                                                    tooltip="Include your cancellation policy here to keep your customers informed ahead of time on company policy. This will show on the Confirmation and Reminder email"
                                                >
                                                    <TextareaField rows={6} />
                                                    <div className="mt-2 flex space-x-2">
                                                        <Switch
                                                            size="medium"
                                                            isChecked={values.cancellation?.refundable}
                                                            onChange={(value: boolean) =>
                                                                setFieldValue("cancellation", {
                                                                    ...values.cancellation,
                                                                    refundable: value,
                                                                })
                                                            }
                                                        />
                                                        <span>
                                                            My cancellation policy offers refunds, store credits, and/or
                                                            date changes
                                                        </span>
                                                    </div>
                                                </FormField>

                                                <FormField
                                                    name="other"
                                                    label="Other Considerations"
                                                    tooltip="Use this space to include any and all important details that are not covered in their own field that you would like to relay to the customer after they book in the confirmation and reminder email"
                                                >
                                                    <TextareaField rows={6} />
                                                    <ContentLengthCounter value={values.other} />
                                                </FormField>

                                                {experience && (
                                                    <FormField
                                                        name="attachments"
                                                        label="Attachments"
                                                        tooltip="Upload file attachments to be sent in your confirmation email, like a PDF or Waiver for example"
                                                    >
                                                        <ProductAttachmentsUpload
                                                            attachments={experience.attachments}
                                                            productId={experience.id}
                                                            productType={ProductObject.EXPERIENCE}
                                                            handleAttachmentChange={(attachments) =>
                                                                setFieldValue("attachments", attachments)
                                                            }
                                                        />
                                                    </FormField>
                                                )}

                                                <FormField
                                                    name="emailFooter"
                                                    label="Email Footer"
                                                    tooltip="Use the text editor tool to create a footer section that will appear at the bottom of your confirmation and reminder emails"
                                                >
                                                    <EmailFooter
                                                        value={values.emailFooter}
                                                        handleEmailFooterChange={(html: string | null) =>
                                                            setFieldValue("emailFooter", html)
                                                        }
                                                    />
                                                </FormField>
                                            </div>
                                        </fieldset>
                                        <ScrollToFieldError />
                                    </Form>
                                    <ContentLayout.Footer preferenceSidebar>
                                        <div className="flex items-center space-x-2">
                                            <SubmitButton isLoading={isSubmitting} onClick={handleSubmit}>
                                                {experience ? "Save" : "Next"}
                                            </SubmitButton>

                                            <SecondaryButton type="reset" onClick={handleCancel}>
                                                Cancel
                                            </SecondaryButton>
                                        </div>
                                    </ContentLayout.Footer>

                                    <ScrollToFieldError />
                                </ContentLayout>
                            );
                        }}
                    </XolaFormik>
                </div>
            </div>
        </div>
    );
};

interface MeetingLocationLabelProps {
    errors: FormikErrors<ExperienceDescriptionFormValues>;
    isMapEnabled: boolean;
}

const MeetingLocationLabel = ({ errors, isMapEnabled }: MeetingLocationLabelProps) => {
    const hasCoordinatesError = isMapEnabled && !!(errors.meetingLocation ?? errors.geo);

    return (
        <div className={clsx(hasCoordinatesError && "!text-danger", "text-sm font-bold")}>
            Meeting Location {hasCoordinatesError ? "coordinates are required" : ""}
        </div>
    );
};

const getInitialValues = (
    seller: Seller,
    experience?: ExperienceDescriptionFormValues,
    addedCollectionPaths: string[] = [],
): ExperienceDescriptionFormValues => {
    const defaultExperience: Omit<Experience, "id"> = getDefaultExperience(seller);

    return produce(
        {
            ...defaultExperience,
            enableMap: hasMappedMeetingLocation(experience),
            ...experience,
            // Set collections path from already added collection nodes
            collections: addedCollectionPaths,
        },
        (draft) => {
            if (isPurchaseItemizationEnabled(seller)) {
                draft.scheduleType = ScheduleType.OPEN;
            }
        },
    );
};
